import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../api/client';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      console.log('🔍 Checking auth via cookie...');
      const response = await authAPI.getMe();
      console.log('✅ Auth check successful:', response.data.email);
      setUser(response.data);
    } catch (error) {
      console.error('❌ Auth check failed:', error.message);
      // Only clear user if it's truly invalid (401), not on network errors
      if (error.response?.status === 401) {
        console.log('🔓 Clearing user due to 401');
        setUser(null);
      } else {
        console.log('⚠️ Network error, keeping user state');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  const login = (userData) => {
    // Cookie is set by backend, just update user state
    console.log('👤 Setting user in context:', userData.email);
    setUser(userData);
  };

  const logout = async () => {
    console.log('🚪 Logging out...');
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      window.location.href = '/login';
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
